import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, CardHeader, Button, Icon, Modal, ModalClose } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { EmailPreview, EMAIL_CANVAS } from '../EmailPreview';
import VendorSelectionModal from '../../../vendor-regularization/components/VendorSelectionModal';
import { useToast } from '../../../../components/ui/Toast';
import { campaignService, getErrorMessage } from '../../../../services/apiService';

/**
 * Broadcast composer — the portal screen for "write a notice, review it, send it".
 *
 * One identical message goes to a chosen vendor audience with only the greeting
 * personalised, which is what separates it from Regularization (per-vendor secure
 * links). It drives two real endpoints:
 *   POST /campaigns/broadcast/preview → renders the exact mail + counts the audience,
 *                                       writing nothing, so "Review" is truthful.
 *   POST /campaigns/broadcast         → creates the campaign and queues the send.
 *
 * Every content field is optional. Leaving the form untouched sends the built-in
 * partnership notice, so the common case is: pick the audience → Review → Send.
 */

/** Blank-line separated block → array of paragraphs. */
function toParagraphs(text) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim().replace(/\n+/g, ' '))
    .filter(Boolean);
}

/** One item per line. */
function toLines(text) {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

const FIELD =
  'w-full rounded-xl border border-cos-line bg-white/70 px-3 py-2.5 text-sm text-cos-ink placeholder:text-cos-ink-secondary/60 focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-cos-ink';
const HINT = 'mt-1 text-[12px] leading-[17px] text-cos-ink-secondary';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
      {hint ? <p className={HINT}>{hint}</p> : null}
    </div>
  );
}

export function BroadcastComposer() {
  const toast = useToast();
  const navigate = useNavigate();

  // Audience
  const [vendorIds, setVendorIds] = useState([]);
  const [allActive, setAllActive] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);

  // Content (all optional — empty means "use the built-in partnership notice")
  const [subject, setSubject] = useState('');
  const [eyebrow, setEyebrow] = useState('');
  const [heading, setHeading] = useState('');
  const [lead, setLead] = useState('');
  const [paragraphs, setParagraphs] = useState('');
  const [calloutTitle, setCalloutTitle] = useState('');
  const [calloutLines, setCalloutLines] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [previewText, setPreviewText] = useState('');

  const [preview, setPreview] = useState(null); // { subject, html, recipient_count, sample_recipients }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const audienceReady = allActive || vendorIds.length > 0;

  /** Only send fields the operator actually filled in, so backend defaults apply. */
  const body = useMemo(() => {
    const b = {};
    if (allActive) b.all_active = true;
    else if (vendorIds.length) b.vendor_ids = vendorIds;
    if (subject.trim()) b.subject = subject.trim();
    if (eyebrow.trim()) b.eyebrow = eyebrow.trim();
    if (heading.trim()) b.heading = heading.trim();
    if (lead.trim()) b.lead = lead.trim();
    const paras = toParagraphs(paragraphs);
    if (paras.length) b.paragraphs = paras;
    if (calloutTitle.trim()) b.callout_title = calloutTitle.trim();
    const lines = toLines(calloutLines);
    if (lines.length) b.callout_lines = lines;
    if (ctaLabel.trim() && ctaUrl.trim()) {
      b.cta_label = ctaLabel.trim();
      b.cta_url = ctaUrl.trim();
    }
    if (previewText.trim()) b.preview = previewText.trim();
    return b;
  }, [allActive, vendorIds, subject, eyebrow, heading, lead, paragraphs, calloutTitle, calloutLines, ctaLabel, ctaUrl, previewText]);

  const ctaHalfFilled = Boolean(ctaLabel.trim()) !== Boolean(ctaUrl.trim());

  const loadPreview = useCallback(async () => {
    if (!audienceReady) {
      toast.warning('Choose an audience first');
      return;
    }
    if (ctaHalfFilled) {
      toast.warning('A button needs both a label and a link');
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await campaignService.previewBroadcast(body);
      if (res.success) setPreview(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Could not render the preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [audienceReady, ctaHalfFilled, body, toast]);

  /** Sending is irreversible, so it is gated behind an explicit confirmation. */
  const askToSend = () => {
    if (!preview) {
      toast.warning('Review the message before sending');
      return;
    }
    setConfirming(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await campaignService.sendBroadcast(body);
      if (res.success) {
        setConfirming(false);
        toast.success(`${res.message} — track delivery in Campaigns`);
        navigate('/campaigning-tool/campaigns', { state: { refreshAt: Date.now() } });
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to send the broadcast');
    } finally {
      setSending(false);
    }
  };

  usePageChrome(
    () => ({
      title: 'Broadcast',
      subtitle: 'Compose a notice, review the exact email, then send it to your vendors',
      actions: (
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon="visibility" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? 'Rendering…' : 'Review'}
          </Button>
          <Button variant="primary" icon="send" onClick={askToSend} disabled={!preview || sending}>
            {sending ? 'Sending…' : 'Send broadcast'}
          </Button>
        </div>
      ),
    }),
    [loadPreview, loadingPreview, preview, sending]
  );

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* ---------------------------------------------------------------- compose */}
      <div className="flex flex-col gap-5">
        <GlassCard>
          <CardHeader
            title="Audience"
            subtitle="Who receives this notice"
            icon={<Icon name="groups" className="text-[20px] text-cos-teal-deep" />}
          />
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={vendorIds.length && !allActive ? 'primary' : 'secondary'}
                icon="checklist"
                onClick={() => {
                  setAllActive(false);
                  setShowVendorModal(true);
                }}
              >
                {vendorIds.length && !allActive ? `${vendorIds.length} vendor(s) selected` : 'Choose vendors'}
              </Button>
              <Button
                variant={allActive ? 'primary' : 'secondary'}
                icon="public"
                onClick={() => {
                  setAllActive((v) => !v);
                  setVendorIds([]);
                  setPreview(null);
                }}
              >
                All active vendors
              </Button>
            </div>
            {allActive ? (
              <div className="flex items-start gap-2 rounded-xl border border-cos-status-amber/30 bg-cos-status-amber-bg px-3 py-2.5">
                <Icon name="warning" className="mt-0.5 text-[18px] text-cos-status-amber" />
                <p className="text-[13px] leading-[19px] text-cos-ink">
                  This will email <strong>every active vendor</strong>. Review the message carefully — the recipient count is
                  shown on the preview before you can send.
                </p>
              </div>
            ) : null}
          </div>
        </GlassCard>

        <GlassCard>
          <CardHeader
            title="Message"
            subtitle="Leave everything blank to send the standard Genessence partnership notice"
            icon={<Icon name="edit_note" className="text-[20px] text-cos-teal-deep" />}
            action={
              <Button
                variant="ghost"
                size="sm"
                icon="restart_alt"
                onClick={() => {
                  setSubject('');
                  setEyebrow('');
                  setHeading('');
                  setLead('');
                  setParagraphs('');
                  setCalloutTitle('');
                  setCalloutLines('');
                  setCtaLabel('');
                  setCtaUrl('');
                  setPreviewText('');
                  setPreview(null);
                }}
              >
                Reset to default
              </Button>
            }
          />
          <div className="flex flex-col gap-4">
            <Field label="Subject" hint="Shown in the inbox. Blank uses the default partnership subject.">
              <input className={FIELD} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Important Notice: Genessence is now Amber’s technical partner" />
            </Field>

            <Field label="Preview text" hint="The grey line inbox clients show next to the subject.">
              <input className={FIELD} value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Genessence Consulting is now Amber’s technical partner…" />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_minmax(0,1fr)]">
              <Field label="Eyebrow">
                <input className={FIELD} value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} placeholder="Important notice" />
              </Field>
              <Field label="Headline">
                <input className={FIELD} value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Genessence is now our technical partner…" />
              </Field>
            </div>

            <Field label="Opening line" hint="Sits under the headline in a slightly larger size.">
              <textarea className={FIELD} rows={2} value={lead} onChange={(e) => setLead(e.target.value)} placeholder="We are writing to let you know about a change in how Amber manages its vendor records…" />
            </Field>

            <Field label="Body" hint="Separate paragraphs with a blank line. Basic HTML such as <strong> is allowed.">
              <textarea className={FIELD} rows={7} value={paragraphs} onChange={(e) => setParagraphs(e.target.value)} placeholder={'First paragraph.\n\nSecond paragraph.'} />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <Field label="Highlight box title">
                <input className={FIELD} value={calloutTitle} onChange={(e) => setCalloutTitle(e.target.value)} placeholder="What we ask you to do" />
              </Field>
              <Field label="Highlight box points" hint="One per line.">
                <textarea className={FIELD} rows={3} value={calloutLines} onChange={(e) => setCalloutLines(e.target.value)} placeholder={'Add us to your safe-sender list.\nAsk IT to allow our mail.'} />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <Field label="Button label">
                <input className={FIELD} value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder="Optional" />
              </Field>
              <Field label="Button link">
                <input className={FIELD} value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://…" />
              </Field>
            </div>
            {ctaHalfFilled ? (
              <p className="-mt-1 text-[12px] font-medium text-cos-status-coral">A button needs both a label and a link.</p>
            ) : null}
          </div>
        </GlassCard>
      </div>

      {/* ---------------------------------------------------------------- review */}
      <div className="flex flex-col gap-5">
        <GlassCard>
          <CardHeader
            title="Review"
            subtitle={preview ? `${preview.recipient_count} recipient(s)` : 'Render the exact email before sending'}
            icon={<Icon name="mark_email_read" className="text-[20px] text-cos-teal-deep" />}
            action={
              <Button variant="secondary" size="sm" icon="refresh" onClick={loadPreview} disabled={loadingPreview}>
                {loadingPreview ? 'Rendering…' : 'Refresh'}
              </Button>
            }
          />
          {preview ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-xl border border-cos-line bg-white/60 px-3 py-2.5">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-cos-ink-secondary">Subject</p>
                <p className="mt-0.5 text-sm font-semibold text-cos-ink">{preview.subject}</p>
                {preview.sample_recipients?.length ? (
                  <p className="mt-2 text-[12px] leading-[18px] text-cos-ink-secondary">
                    Going to {preview.sample_recipients.join(', ')}
                    {preview.recipient_count > preview.sample_recipients.length
                      ? ` and ${preview.recipient_count - preview.sample_recipients.length} more`
                      : ''}
                  </p>
                ) : null}
              </div>
              {/* The whole mail at true size — no scrollbars inside the preview. */}
              <div className="overflow-hidden rounded-xl border border-cos-line" style={{ background: EMAIL_CANVAS }}>
                <EmailPreview html={preview.html} title="Email preview" />
              </div>
              <p className="text-[12px] leading-[17px] text-cos-ink-secondary">
                Merge fields such as the company name appear as <code>{'{{params.company_name}}'}</code> here; they are filled in per recipient
                in per recipient when the mail is sent.
              </p>
            </div>
          ) : (
            <div className="flex h-[420px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-cos-line bg-white/40 text-center">
              <Icon name="drafts" className="text-[34px] text-cos-ink-secondary/50" />
              <p className="max-w-[280px] text-sm text-cos-ink-secondary">
                Choose an audience and press <strong className="text-cos-ink">Review</strong> to render the exact email your
                vendors will receive.
              </p>
            </div>
          )}
        </GlassCard>
      </div>

      <Modal open={confirming} onClose={() => !sending && setConfirming(false)} size="md" labelledBy="broadcast-confirm">
        <div className="p-6">
          <ModalClose onClose={() => !sending && setConfirming(false)} />
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-status-amber-bg">
              <Icon name="campaign" className="text-[21px] text-cos-status-amber" />
            </span>
            <div className="min-w-0">
              <h2 id="broadcast-confirm" className="text-base font-bold text-cos-ink">
                Send this broadcast?
              </h2>
              <p className="mt-1 text-sm leading-[21px] text-cos-ink-secondary">
                It goes to <strong className="text-cos-ink">{preview?.recipient_count}</strong> vendor
                {preview?.recipient_count === 1 ? '' : 's'} as
                <span className="text-cos-ink"> “{preview?.subject}”</span>. Emails cannot be recalled once sent.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={sending}>
              Cancel
            </Button>
            <Button variant="primary" icon="send" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : `Send to ${preview?.recipient_count ?? 0}`}
            </Button>
          </div>
        </div>
      </Modal>

      {showVendorModal ? (
        <VendorSelectionModal
          title="Choose the audience"
          subtitle="Pick the vendors who should receive this notice"
          confirmLabel="Use these vendors"
          onClose={() => setShowVendorModal(false)}
          onConfirm={(ids) => {
            setVendorIds(ids);
            setAllActive(false);
            setPreview(null);
            setShowVendorModal(false);
          }}
        />
      ) : null}
    </div>
  );
}

export default BroadcastComposer;
